import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header.js";
import FooterComponent from "../components/FooterComponent.js";
import { useProducts } from "../contexts/ProductsContext";
import { useLanguage } from "../contexts/LanguageContext";
import { cloudinaryUrl, PRODUCTS_FOLDER } from "../utils/cloudinary";
import CloudinaryImage from "../components/CloudinaryImage";

const Collections = () => {
  const { language } = useLanguage();
  const { products, loading, error } = useProducts();
  const getProductsByCollection = (collectionName) =>
    (products ?? []).filter(
      (product) =>
        product.collection_name.toLowerCase() ===
          collectionName.toLowerCase() && product.availability,
    );

  return (
    <div className="bg-cream min-h-screen">
      <Header />

      <div className="text-center py-20 px-6">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4 font-light">
          {language === "pt" ? "Explorar" : "Explore"}
        </p>
        <h1 className="font-display text-5xl md:text-6xl text-stone-800 mb-4">
          {language === "pt" ? "As Nossas Coleções" : "Our Collections"}
        </h1>
        <p className="text-stone-400 font-light max-w-md mx-auto text-sm leading-relaxed">
          {language === "pt"
            ? "Cada viagem, uma experiência única. Cada país, um visual diferente."
            : "Each trip, a unique experience. Each country, a different look."}
        </p>
      </div>

      {error && (
        <div className="max-w-6xl mx-auto px-6 pb-20 text-center text-stone-400">
          {language === "pt"
            ? "Não foi possível carregar as coleções."
            : "Could not load the collections."}
        </div>
      )}
      {!error && loading && (
        <div className="max-w-6xl mx-auto px-6 pb-20 text-center text-stone-400">
          {language === "pt" ? "A carregar…" : "Loading…"}
        </div>
      )}

      <CollectionSection
        title="Globe"
        description={
          language === "pt"
            ? "Peças complementares para muitos visuais diferentes."
            : "Complementary pieces for many different looks."
        }
        products={getProductsByCollection("globe")}
      />
      <CollectionSection
        title="Mozambique"
        description={
          language === "pt" ? "O meu país de origem." : "My country of origin."
        }
        products={getProductsByCollection("mozambique")}
      />
      <CollectionSection
        title="China"
        description={
          language === "pt"
            ? "Peças baseadas em milhares de anos de tradições."
            : "Pieces based on thousands of years of traditions."
        }
        products={getProductsByCollection("china")}
      />
      <CollectionSection
        title="India"
        description={
          language === "pt"
            ? "A terra das especiarias e elefantes."
            : "The land of spices and elephants."
        }
        products={getProductsByCollection("india")}
      />
      <CollectionSection
        title="Seychelles"
        description={
          language === "pt"
            ? "Clima quente e palmeiras."
            : "Warm weather and palmtrees."
        }
        products={getProductsByCollection("seychelles")}
      />
      <CollectionSection
        title="Indonesia"
        description={
          language === "pt"
            ? "Conhecida pelo seu café e praias."
            : "Known for it's coffee and beaches."
        }
        products={getProductsByCollection("indonesia")}
      />
      <CollectionSection
        title="Silver"
        description={
          language === "pt"
            ? "Peças em prata esterlina com brilho intemporal."
            : "Sterling silver pieces with timeless shine."
        }
        products={getProductsByCollection("silver")}
      />
      <CollectionSection
        title="Mens"
        description={
          language === "pt"
            ? "Designs marcantes e masculinos para o homem moderno."
            : "Bold, masculine designs for the modern man."
        }
        products={getProductsByCollection("mens")}
      />

      <FooterComponent />
    </div>
  );
};

const CollectionSection = ({ title, description, products }) => {
  if (products.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-6 pb-20">
      <div className="flex flex-col items-center text-center mb-10">
        <h2 className="font-display text-4xl md:text-5xl text-stone-800 mb-3">
          {title}
        </h2>
        <p className="text-stone-400 font-light text-sm max-w-sm">
          {description}
        </p>
        <div className="mt-5 h-px w-16 bg-gold-light" />
      </div>
      <div className="grid gap-8 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 w-full">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
};

const ProductCard = ({ product }) => {
  const { language } = useLanguage();

  return (
    <Link to={`/product/${product.id}`} className="group">
      <div className="relative overflow-hidden aspect-square bg-stone-100">
        {product.photos && product.photos.length > 0 ? (
          <CloudinaryImage
            src={cloudinaryUrl(product.photos[0], PRODUCTS_FOLDER, 600)}
            alt={
              typeof product.name === "object"
                ? product.name[language]
                : product.name
            }
            wrapperClassName="h-full w-full"
            imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-stone-100 text-stone-300">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-colors duration-300" />
      </div>
      <div className="pt-4 pb-2">
        <h3 className="text-sm tracking-wide text-stone-700 text-center font-light group-hover:text-gold-dark transition-colors duration-200">
          {typeof product.name === "object"
            ? product.name[language]
            : product.name}
        </h3>
      </div>
    </Link>
  );
};

export default Collections;
